import AdminShell from "@/components/AdminShell";
/**
 * KISSED University · Admin Enrollment Dashboard.
 * Route: /admin/university/enrollments
 *
 * Super-admin paged view of all enrollments with:
 *   • filters (program, status, email)
 *   • status transitions (active / on_hold / withdrawn / expelled / graduated)
 *   • admin-initiated enrollment assignment (assign a student to a program)
 *   • per-enrollment course-purchase add (Pay-Per-Course)
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ChevronLeft, Loader2, RefreshCw, Filter, GraduationCap,
  Plus, Save, Pencil, Receipt,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TopNav } from "@/components/Layout";
import { adminAuth } from "@/lib/api";
import { universityApi } from "@/lib/universityApi";
import { formatApiError } from "@/lib/formatError";

const STATUSES = [
  "started", "pending_payment", "pending", "active", "on_hold",
  "graduated", "withdrawn", "expelled",
];
const STATUS_TONE = {
  started: "bg-slate-100 text-slate-700",
  pending_payment: "bg-amber-100 text-amber-800",
  pending: "bg-purple-100 text-purple-800",
  active: "bg-emerald-100 text-emerald-800",
  on_hold: "bg-yellow-100 text-yellow-800",
  graduated: "bg-emerald-200 text-emerald-900",
  withdrawn: "bg-slate-200 text-slate-700",
  expelled: "bg-red-100 text-red-800",
};

const usd = (n) =>
  n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

export default function AdminUniversityEnrollments() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [filterProgram, setFilterProgram] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterEmail, setFilterEmail] = useState("");
  const [assignOpen, setAssignOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [editing, setEditing] = useState(null); // enrollment id

  useEffect(() => {
    if (!adminAuth.getToken()) { navigate("/admin/login"); return; }
    if (!adminAuth.isSuperAdmin()) {
      toast.error("Super admin access required.");
      navigate("/admin");
      return;
    }
    bootstrap();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = useCallback(async () => {
    try {
      setRefreshing(true);
      const r = await universityApi.adminEnrollmentList({
        ...(filterProgram ? { program_key: filterProgram } : {}),
        ...(filterStatus ? { status: filterStatus } : {}),
        ...(filterEmail ? { email: filterEmail } : {}),
      });
      setItems(r.data?.items || []);
    } catch (err) {
      toast.error(formatApiError(err, "Failed to load enrollments"));
    } finally {
      setRefreshing(false);
    }
  }, [filterProgram, filterStatus, filterEmail]);

  const bootstrap = async () => {
    setLoading(true);
    try {
      const [opts] = await Promise.all([universityApi.options()]);
      setPrograms(opts.data?.program_pricing || []);
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err, "Failed to load"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminShell door="university" title="Enrollments">
      <TopNav
        right={
          <Link to="/admin/university">
            <Button variant="ghost" className="rounded-full text-slate-700" data-testid="back-to-admissions">
              <ChevronLeft className="h-4 w-4 mr-1" /> Admissions queue
            </Button>
          </Link>
        }
      />
      <div>
        <div className="kcis-overline text-purple-900">KISSED University</div>
        <h1 className="font-display text-3xl sm:text-4xl text-slate-900 mt-2 flex items-center gap-3">
          <GraduationCap className="w-7 h-7 text-purple-900" /> Enrollments
        </h1>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">
          Every student enrollment across all programs. Use the filters to narrow the list, change status as
          students progress, or manually assign a student to a program.
        </p>

        {/* Toolbar */}
        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="grow min-w-[180px]">
              <Label className="text-[11px] uppercase tracking-wide text-slate-500">Program</Label>
              <select
                data-testid="enr-filter-program"
                value={filterProgram}
                onChange={(e) => setFilterProgram(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
              >
                <option value="">All programs</option>
                {programs.map((p) => (
                  <option key={p.option} value={p.option}>{p.option}</option>
                ))}
              </select>
            </div>
            <div className="grow min-w-[160px]">
              <Label className="text-[11px] uppercase tracking-wide text-slate-500">Status</Label>
              <select
                data-testid="enr-filter-status"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
              >
                <option value="">All statuses</option>
                {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
              </select>
            </div>
            <div className="grow min-w-[220px]">
              <Label className="text-[11px] uppercase tracking-wide text-slate-500">Email</Label>
              <Input
                data-testid="enr-filter-email"
                value={filterEmail}
                onChange={(e) => setFilterEmail(e.target.value)}
                placeholder="student@example.com"
                className="mt-1"
              />
            </div>
            <Button variant="outline" onClick={refresh} disabled={refreshing} data-testid="enr-refresh-btn">
              {refreshing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Filter className="w-4 h-4 mr-1" />}Apply
            </Button>
            <Button className="bg-purple-900 hover:bg-purple-800" onClick={() => setAssignOpen(true)} data-testid="enr-assign-open-btn">
              <Plus className="w-4 h-4 mr-1" /> Assign student
            </Button>
          </div>
        </div>

        {/* Assign student modal (inline form) */}
        {assignOpen && (
          <AdminAssignStudent
            programs={programs}
            onClose={() => setAssignOpen(false)}
            onCreated={async () => { setAssignOpen(false); await refresh(); }}
          />
        )}

        {/* List */}
        <div className="mt-6 space-y-3" data-testid="enr-list">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : items.length === 0 ? (
            <div className="rounded-2xl border border-slate-200 bg-white p-8 text-sm text-slate-600 text-center">
              No enrollments match the current filters.
            </div>
          ) : (
            items.map((e) => (
              <EnrollmentRow
                key={e.id}
                enrollment={e}
                onChanged={refresh}
                editing={editing === e.id}
                onToggleEdit={() => setEditing(editing === e.id ? null : e.id)}
              />
            ))
          )}
        </div>
      </div>
    </AdminShell>
  );
}

function EnrollmentRow({ enrollment: e, onChanged, editing, onToggleEdit }) {
  const [status, setStatus] = useState(e.status);
  const [notes, setNotes] = useState(e.admin_notes || "");
  const [saving, setSaving] = useState(false);
  const [courseOpen, setCourseOpen] = useState(false);
  const [lmsOpen, setLmsOpen] = useState(false);
  const [progress, setProgress] = useState(null);
  const [busyCode, setBusyCode] = useState("");
  const courses = e.purchased_courses || [];

  const loadProgress = useCallback(async () => {
    try {
      const r = await universityApi.enrollmentProgress(e.id);
      setProgress(r.data);
    } catch (err) {
      toast.error(formatApiError(err, "Failed to load progress"));
    }
  }, [e.id]);

  const toggleLms = async () => {
    const next = !lmsOpen;
    setLmsOpen(next);
    if (next && !progress) await loadProgress();
  };

  const toggleCompletion = async (code, completed) => {
    setBusyCode(code);
    try {
      const r = await universityApi.adminCourseCompletion(e.id, { course_code: code, completed });
      if (r.data?.graduated) toast.success("Program complete — student graduated!");
      else toast.success(completed ? `Marked ${code} complete` : `Cleared ${code}`);
      await loadProgress();
      await onChanged();
    } catch (err) {
      toast.error(formatApiError(err, "Update failed"));
    } finally {
      setBusyCode("");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await universityApi.adminEnrollmentPatch(e.id, { status, admin_notes: notes });
      toast.success("Enrollment updated.");
      await onChanged();
      onToggleEdit();
    } catch (err) {
      toast.error(formatApiError(err, "Update failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`enr-row-${e.id}`}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-xs text-slate-500">{e.public_enrollment_id || e.id.slice(0, 8)}</span>
            <span className={`text-[11px] px-2 py-0.5 rounded-full ${STATUS_TONE[e.status] || "bg-slate-100 text-slate-700"}`} data-testid={`enr-row-status-${e.id}`}>
              {e.status.replace("_", " ")}
            </span>
            <span className="text-[11px] uppercase tracking-wide text-purple-900">{(e.program_type || "").replace("_", " ")}</span>
            <span className="text-[11px] text-slate-500">{e.payment_plan?.replace("_", " ")}</span>
          </div>
          <div className="font-display text-base text-slate-900 mt-1">{e.program_name}</div>
          <div className="text-xs text-slate-500 mt-0.5">
            <span data-testid={`enr-row-email-${e.id}`}>{e.student_email}</span>
            {e.student_name ? ` · ${e.student_name}` : ""}
            {e.started_at ? ` · started ${new Date(e.started_at).toLocaleDateString()}` : ""}
          </div>
        </div>
        <div className="text-right text-sm">
          <div className="text-slate-900 font-medium">{usd(e.tuition_paid_usd)} <span className="text-xs text-slate-400">/ {usd(e.tuition_total_usd || (e.tuition_paid_usd + e.tuition_balance_usd))}</span></div>
          <div className="text-[11px] text-slate-500">Balance {usd(e.tuition_balance_usd)}</div>
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <Button variant="ghost" size="sm" onClick={onToggleEdit} data-testid={`enr-row-edit-${e.id}`}>
          <Pencil className="w-3.5 h-3.5 mr-1" />{editing ? "Cancel" : "Edit status"}
        </Button>
        {(e.payment_plan === "pay_per_course" || e.payment_plan === "term_payment") && (
          <Button variant="ghost" size="sm" onClick={() => setCourseOpen(!courseOpen)} data-testid={`enr-row-add-course-${e.id}`}>
            <Receipt className="w-3.5 h-3.5 mr-1" />
            {courseOpen ? "Cancel" : (e.payment_plan === "term_payment" ? "Record term payment" : "Add course purchase")}
          </Button>
        )}
        <Button variant="ghost" size="sm" onClick={toggleLms} data-testid={`enr-row-lms-${e.id}`}>
          <GraduationCap className="w-3.5 h-3.5 mr-1" />
          {lmsOpen ? "Hide course list" : "Manage course completion"}
        </Button>
      </div>

      {editing && (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <Label className="text-[11px] uppercase tracking-wide text-slate-500">Status</Label>
            <select
              data-testid={`enr-row-status-select-${e.id}`}
              value={status}
              onChange={(ev) => setStatus(ev.target.value)}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
            >
              {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <Label className="text-[11px] uppercase tracking-wide text-slate-500">Admin notes</Label>
            <Input data-testid={`enr-row-notes-${e.id}`} className="mt-1" value={notes} onChange={(ev) => setNotes(ev.target.value)} />
          </div>
          <div className="md:col-span-3 flex justify-end">
            <Button onClick={save} disabled={saving} className="bg-purple-900 hover:bg-purple-800" data-testid={`enr-row-save-${e.id}`}>
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}Save
            </Button>
          </div>
        </div>
      )}

      {courseOpen && (
        <AdminAddCourse enrollmentId={e.id} onClose={() => setCourseOpen(false)} onAdded={async () => { setCourseOpen(false); await onChanged(); }} />
      )}

      {lmsOpen && (
        <div className="mt-3 border-t border-slate-100 pt-3" data-testid={`enr-lms-panel-${e.id}`}>
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] uppercase tracking-wide text-slate-500">
              Course completion
              {progress?.credential && (
                <span className="ml-2 text-purple-900 font-medium normal-case tracking-normal">
                  → {progress.credential}
                </span>
              )}
            </div>
            {progress && (
              <div className="text-xs text-slate-700">
                {progress.courses_completed}/{progress.courses_total} ({progress.progress_percent}%)
              </div>
            )}
          </div>
          {!progress ? (
            <div className="text-xs text-slate-500"><Loader2 className="w-3 h-3 inline animate-spin mr-1" />Loading…</div>
          ) : (progress.courses || []).length === 0 ? (
            <div className="text-xs text-slate-500">No course list configured for this program yet.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
              {(progress.courses || []).map((c) => (
                <label
                  key={c.code}
                  className={`flex items-center gap-2 text-xs rounded-lg px-3 py-1.5 border ${
                    c.completed ? "bg-emerald-50 border-emerald-200" :
                    c.credited ? "bg-purple-50 border-purple-200" :
                    c.locked ? "bg-slate-50 border-slate-200 text-slate-400" :
                    "bg-white border-slate-200"
                  }`}
                  data-testid={`enr-lms-course-${e.id}-${c.code}`}
                >
                  <input
                    type="checkbox"
                    checked={c.completed}
                    disabled={busyCode === c.code || c.credited}
                    onChange={(ev) => toggleCompletion(c.code, ev.target.checked)}
                    data-testid={`enr-lms-check-${e.id}-${c.code}`}
                  />
                  <span className="font-mono text-[11px] text-slate-500">{c.code}</span>
                  <span className="flex-1 truncate">{c.name}</span>
                  {c.credited && <span className="text-[10px] uppercase tracking-wide text-purple-700">credited</span>}
                  {c.locked && !c.credited && <span className="text-[10px] uppercase tracking-wide text-slate-400">locked</span>}
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      {courses.length > 0 && (
        <div className="mt-3 border-t border-slate-100 pt-3">
          <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1.5">Courses purchased</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
            {courses.map((c, idx) => (
              <div key={idx} className="text-xs text-slate-700 bg-slate-50 rounded-lg px-3 py-1.5 flex justify-between" data-testid={`enr-row-course-${e.id}-${idx}`}>
                <span><span className="font-mono">{c.course_code}</span> {c.course_name ? `· ${c.course_name}` : ""}</span>
                <span className="text-slate-500">{usd(c.amount_paid_usd)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AdminAddCourse({ enrollmentId, onClose, onAdded }) {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [ref, setRef] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (ev) => {
    ev.preventDefault();
    if (!code.trim()) { toast.error("Course code is required."); return; }
    setSaving(true);
    try {
      await universityApi.enrollmentAddCourse(enrollmentId, {
        course_code: code.trim(),
        course_name: name.trim(),
        amount_paid_usd: parseFloat(amount) || 0,
        payment_reference: ref.trim(),
      });
      toast.success("Course purchase recorded.");
      await onAdded();
    } catch (err) {
      toast.error(formatApiError(err, "Could not add course"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-3 rounded-xl border border-purple-100 bg-purple-50/40 p-3 grid grid-cols-1 md:grid-cols-4 gap-3 items-end" data-testid={`enr-row-course-form-${enrollmentId}`}>
      <div>
        <Label className="text-[11px] uppercase tracking-wide text-slate-500">Course code</Label>
        <Input data-testid={`enr-course-code-${enrollmentId}`} className="mt-1" value={code} onChange={(e) => setCode(e.target.value)} placeholder="TRI-101" />
      </div>
      <div>
        <Label className="text-[11px] uppercase tracking-wide text-slate-500">Course name</Label>
        <Input data-testid={`enr-course-name-${enrollmentId}`} className="mt-1" value={name} onChange={(e) => setName(e.target.value)} placeholder="Hair Anatomy" />
      </div>
      <div>
        <Label className="text-[11px] uppercase tracking-wide text-slate-500">Amount paid</Label>
        <Input data-testid={`enr-course-amount-${enrollmentId}`} className="mt-1" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" />
      </div>
      <div>
        <Label className="text-[11px] uppercase tracking-wide text-slate-500">Payment ref</Label>
        <Input data-testid={`enr-course-ref-${enrollmentId}`} className="mt-1" value={ref} onChange={(e) => setRef(e.target.value)} placeholder="sq-…" />
      </div>
      <div className="md:col-span-4 flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
        <Button type="submit" className="bg-purple-900 hover:bg-purple-800" disabled={saving} data-testid={`enr-course-save-${enrollmentId}`}>
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}Record purchase
        </Button>
      </div>
    </form>
  );
}

function AdminAssignStudent({ programs, onClose, onCreated }) {
  const [program, setProgram] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [plan, setPlan] = useState("pay_in_full");
  const [status, setStatus] = useState("active");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (ev) => {
    ev.preventDefault();
    if (!program) { toast.error("Pick a program."); return; }
    if (!email.trim()) { toast.error("Student email is required."); return; }
    setSaving(true);
    try {
      await universityApi.adminEnrollmentCreate({
        student_email: email.trim(),
        student_name: name.trim(),
        program_key: program,
        payment_plan: plan,
        status,
        admin_notes: notes,
      });
      toast.success("Student enrolled.");
      await onCreated();
    } catch (err) {
      toast.error(formatApiError(err, "Could not enroll student"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 rounded-2xl border border-purple-200 bg-white p-5" data-testid="enr-assign-form">
      <h2 className="font-display text-lg text-slate-900">Assign student to a program</h2>
      <form onSubmit={submit} className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="md:col-span-2">
          <Label className="text-[11px] uppercase tracking-wide text-slate-500">Program</Label>
          <select
            data-testid="enr-assign-program"
            value={program}
            onChange={(e) => setProgram(e.target.value)}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
          >
            <option value="">Choose a program…</option>
            {programs.map((p) => (
              <option key={p.option} value={p.option}>{p.option} — {p.division}</option>
            ))}
          </select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wide text-slate-500">Student email</Label>
          <Input data-testid="enr-assign-email" className="mt-1" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wide text-slate-500">Student name</Label>
          <Input data-testid="enr-assign-name" className="mt-1" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wide text-slate-500">Payment plan</Label>
          <select
            data-testid="enr-assign-plan"
            value={plan}
            onChange={(e) => setPlan(e.target.value)}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
          >
            <option value="pay_in_full">Pay In Full</option>
            <option value="pay_per_course">Pay Per Course</option>
            <option value="term_payment">Term Payment</option>
            <option value="undecided">Undecided</option>
          </select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wide text-slate-500">Initial status</Label>
          <select
            data-testid="enr-assign-status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
          >
            {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
          </select>
        </div>
        <div className="md:col-span-2">
          <Label className="text-[11px] uppercase tracking-wide text-slate-500">Admin notes (optional)</Label>
          <Input data-testid="enr-assign-notes" className="mt-1" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
        <div className="md:col-span-2 flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" className="bg-purple-900 hover:bg-purple-800" disabled={saving} data-testid="enr-assign-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}Assign
          </Button>
        </div>
      </form>
    </div>
  );
}
